import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { STORAGE_KEYS } from "../../utils/constants";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Clock, CheckCircle, XCircle, AlertTriangle, Calendar, IndianRupee,
  Wrench, MapPin, Search, FileText, X, List, Phone, User, Truck,
  RefreshCw, ChevronRight, Filter
} from "lucide-react";

const ServiceHistory = () => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const [serviceHistory, setServiceHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedService, setSelectedService] = useState(null);
  const [showServicesModal, setShowServicesModal] = useState(false);
  const [selectedDetails, setSelectedDetails] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  const API_BASE_URL = import.meta.env.VITE_BASE_URL;

  const getCurrentUser = () => {
    if (user) return user;
    try {
      const cached = localStorage.getItem(STORAGE_KEYS.USER);
      return cached ? JSON.parse(cached) : null;
    } catch { return null; }
  };
  const currentUser = getCurrentUser();

  const normalizeStatus = (status) => {
    const s = String(status || "").trim().toLowerCase();
    if (s.includes("pending"))  return "pending";
    if (s.includes("accepted")) return "accepted";
    if (s.includes("rejected")) return "rejected";
    if (s.includes("assigned")) return "assigned";
    if (s.includes("pick"))     return "vehicle pick";
    if (s.includes("process"))  return "in progress";
    if (s.includes("done"))     return "servicing done";
    if (s.includes("drop"))     return "vehicle drop";
    if (s.includes("complete")) return "completed";
    return "pending";
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    try {
      return new Date(dateStr).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
    } catch { return String(dateStr); }
  };

  const buildLocationText = (b) => {
    if (Number(b.pickup_drop) !== 1) return "Service Center Visit";
    const parts = [b.doorstep_flat_no, b.doorstep_area, b.doorstep_landmark, b.doorstep_city, b.doorstep_pincode || b.pincode].filter(Boolean);
    return parts.length ? parts.join(", ") : "Doorstep Service";
  };

  const mapBooking = (b) => ({
    id: b.booking_id, bookingId: b.booking_id, orderId: `BK${String(b.booking_id).padStart(4, "0")}`,
    userName: b.user?.name || currentUser?.name || "User",
    userPhone: b.user?.phone || currentUser?.phone || currentUser?.phoneNumber || "N/A",
    userEmail: b.user?.email || currentUser?.email || null,
    vehicleNumber: b.vehicle_number || "N/A", vehicleBrand: b.bike_brand || "N/A",
    vehicleModel: b.bike_model || "N/A", fuelType: b.fuel_type || "N/A", kilometer: b.kilometer || null,
    serviceType: b.service_type || "Vehicle Service", issueDescription: b.issue_description || "No additional comments",
    location: buildLocationText(b), date: formatDate(b.service_date || b.created_at),
    time: b.service_time_slot || "N/A", createdAt: formatDate(b.created_at),
    amount: Number(b.total_amount || 0), paymentStatus: b.payment_status || "Pending",
    paymentMethod: (b.payment_type || "Cash").toUpperCase(),
    bookingStatus: b.booking_status || "Pending", status: normalizeStatus(b.booking_status),
    services: Array.isArray(b.services)
      ? b.services.map((s) => ({ serviceId: s.service_id, serviceName: s.service_name || `Service #${s.service_id}`, servicePrice: Number(s.price || 0), quantity: Number(s.quantity || 1) }))
      : [],
    store: b.assignedStore ? { id: b.assignedStore.store_id, name: b.assignedStore.name || "Assigned Store", contact: b.assignedStore.contact || "N/A", address: b.assignedStore.address || "N/A", gst_number: b.assignedStore.gst_number || null } : null,
    centerLocation: Number(b.pickup_drop) !== 1 ? b.center_address || "Visit nearest service center" : null,
  });

  const fetchHistory = async () => {
    if (!isAuthenticated) return;
    const token = localStorage.getItem(STORAGE_KEYS.TOKEN);
    if (!token) { setError("Missing authentication token. Please login again."); setLoading(false); return; }
    try {
      setLoading(true); setError(null);
      const { data } = await axios.get(`${API_BASE_URL}/booking/my`, { headers: { Authorization: `Bearer ${token}` } });
      setServiceHistory((data?.data || []).map(mapBooking));
    } catch (err) {
      console.error("ServiceHistory fetch error:", err);
      setError(err.response?.status === 401 ? "Session expired. Please login again." : "Failed to load service history. Please try again.");
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchHistory(); }, [isAuthenticated]);

  const getStatusConfig = (status) => {
    switch ((status || "").toLowerCase()) {
      case "completed":    return { dot: "bg-green-400", text: "text-green-400", bg: "bg-green-400/10 border-green-400/20", label: "Completed", icon: CheckCircle };
      case "pending":      return { dot: "bg-yellow-400", text: "text-yellow-400", bg: "bg-yellow-400/10 border-yellow-400/20", label: "Pending", icon: Clock };
      case "accepted":     return { dot: "bg-[#FF6B35]", text: "text-[#FF6B35]", bg: "bg-[#FF6B35]/10 border-[#FF6B35]/20", label: "Accepted", icon: CheckCircle };
      case "in progress":
      case "servicing done":
      case "vehicle pick":
      case "vehicle drop":
      case "assigned":     return { dot: "bg-[#FF6B35]", text: "text-[#FF6B35]", bg: "bg-[#FF6B35]/10 border-[#FF6B35]/20", label: status, icon: Wrench };
      case "rejected":     return { dot: "bg-red-400", text: "text-red-400", bg: "bg-red-400/10 border-red-400/20", label: "Rejected", icon: XCircle };
      default:             return { dot: "bg-gray-400", text: "text-gray-400", bg: "bg-gray-400/10 border-gray-400/20", label: status, icon: AlertTriangle };
    }
  };

  const BOOKING_STEPS = [
    { key: "pending", label: "Placed" }, { key: "accepted", label: "Accepted" },
    { key: "vehicle pick", label: "Pickup" }, { key: "in progress", label: "Servicing" },
    { key: "servicing done", label: "Done" }, { key: "vehicle drop", label: "Drop" },
    { key: "completed", label: "Complete" },
  ];
  const getCurrentStep = (status) => BOOKING_STEPS.findIndex((s) => s.key === status);

  const filteredHistory = useMemo(() => serviceHistory.filter((s) => {
    const matchFilter = filter === "all" || s.status === filter;
    const term = searchTerm.toLowerCase();
    const matchSearch = (s.vehicleNumber || "").toLowerCase().includes(term) ||
      (s.serviceType || "").toLowerCase().includes(term) ||
      (s.orderId || "").toLowerCase().includes(term) ||
      String(s.bookingId || "").includes(term);
    return matchFilter && matchSearch;
  }), [serviceHistory, filter, searchTerm]);

  const statusCounts = useMemo(() => {
    const c = { all: serviceHistory.length, pending: 0, accepted: 0, assigned: 0, "vehicle pick": 0, "in progress": 0, "servicing done": 0, "vehicle drop": 0, completed: 0, rejected: 0 };
    serviceHistory.forEach((s) => { if (c[s.status] !== undefined) c[s.status]++; });
    return c;
  }, [serviceHistory]);

  const canViewInvoice = (s) => s?.status === "completed";

  // ── Loading State ────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-4">
        <div className="text-center">
          <div className="w-16 h-16 border-2 border-[#FF6B35]/20 border-t-[#FF6B35] rounded-full animate-spin mx-auto mb-6" />
          <p className="text-gray-400 text-base font-medium">Loading your service history...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-4">
        <div className="text-center bg-[#111111] border border-white/[0.08] rounded-2xl p-10 max-w-md w-full">
          <div className="w-16 h-16 bg-red-500/10 border border-red-500/20 rounded-2xl flex items-center justify-center mx-auto mb-5">
            <AlertTriangle className="h-8 w-8 text-red-400" />
          </div>
          <h3 className="text-xl font-bold text-white mb-2">Something went wrong</h3>
          <p className="text-gray-500 mb-6 text-sm">{error}</p>
          <button onClick={fetchHistory}
            className="px-6 py-3 bg-[#FF6B35] text-white rounded-xl font-semibold hover:bg-[#E85520] transition-colors flex items-center gap-2 mx-auto">
            <RefreshCw className="h-4 w-4" /> Try Again
          </button>
        </div>
      </div>
    );
  }

  // ── Main Render ──────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Top header bar */}
      <div className="bg-[#111111] border-b border-white/[0.06] sticky top-0 z-10 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-[#FF6B35] rounded-xl flex items-center justify-center shadow-lg shadow-[#FF6B35]/20">
                <Clock className="h-4 w-4 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-black text-white">Service History</h1>
                <p className="text-xs text-gray-500">{serviceHistory.length} booking{serviceHistory.length !== 1 ? "s" : ""} total</p>
              </div>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-500 bg-white/[0.04] border border-white/[0.06] rounded-xl px-3 py-2">
              <User className="h-4 w-4 text-[#FF6B35]" />
              <span className="font-medium text-gray-300 hidden sm:block">{currentUser?.name || "User"}</span>
              <span className="text-gray-600 hidden sm:block">•</span>
              <Phone className="h-3.5 w-3.5 text-gray-500 hidden sm:block" />
              <span className="hidden sm:block">{currentUser?.phone || currentUser?.phoneNumber || "N/A"}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {/* Search + Filter */}
        <div className="mb-6 space-y-3">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-600" />
            <input type="text" placeholder="Search by vehicle number, service type, booking ID..."
              value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-11 pr-4 py-3 bg-[#111111] border border-white/[0.08] rounded-xl focus:border-[#FF6B35]/40 focus:outline-none text-white placeholder:text-gray-600 text-sm transition-colors" />
          </div>

          <div className="flex items-center gap-2 overflow-x-auto pb-1 no-scrollbar">
            <Filter className="h-4 w-4 text-gray-600 flex-shrink-0" />
            {["all", "pending", "accepted", "in progress", "completed", "rejected"].map((key) => (
              <button key={key} onClick={() => setFilter(key)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-all flex items-center gap-1.5
                  ${filter === key ? "bg-[#FF6B35] text-white shadow-lg shadow-[#FF6B35]/20" : "bg-[#111111] border border-white/[0.08] text-gray-400 hover:border-[#FF6B35]/30 hover:text-[#FF6B35]"}`}>
                <span className="capitalize">{key}</span>
                <span className={`text-[10px] font-bold ${filter === key ? "text-white/70" : "text-gray-600"}`}>
                  {statusCounts[key] || 0}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Cards */}
        {filteredHistory.length === 0 ? (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            className="text-center py-24 bg-[#111111] border border-white/[0.06] rounded-2xl">
            <div className="w-20 h-20 bg-white/[0.04] rounded-2xl flex items-center justify-center mx-auto mb-5">
              <Wrench className="h-10 w-10 text-gray-700" />
            </div>
            <h3 className="text-xl font-bold text-white mb-2">No Bookings Found</h3>
            <p className="text-gray-600 mb-7 text-sm">
              {searchTerm ? "Try adjusting your search" : "You haven't made any bookings yet"}
            </p>
            <button onClick={() => navigate("/")}
              className="px-6 py-3 bg-[#FF6B35] text-white rounded-xl font-semibold hover:bg-[#E85520] transition-colors shadow-lg shadow-[#FF6B35]/20 inline-flex items-center gap-2">
              Book a Service <ChevronRight className="h-4 w-4" />
            </button>
          </motion.div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {filteredHistory.map((service, i) => {
              const statusCfg = getStatusConfig(service.status);
              const StatusIcon = statusCfg.icon;
              const stepIdx = getCurrentStep(service.status);
              const isDoorstep = service.location !== "Service Center Visit";
              const progress = stepIdx >= 0 ? Math.max(5, ((stepIdx + 1) / BOOKING_STEPS.length) * 100) : 5;

              return (
                <motion.div key={service.id}
                  initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05, duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                  className="bg-[#111111] border border-white/[0.08] rounded-2xl overflow-hidden hover:border-[#FF6B35]/20 transition-all duration-300 hover:shadow-xl hover:shadow-black/40 group flex flex-col">

                  {/* Card top bar - color accent */}
                  <div className={`h-0.5 w-full ${statusCfg.dot}`} />

                  {/* Card Header */}
                  <div className="p-5 pb-4">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-[#FF6B35]/10 rounded-xl flex items-center justify-center">
                          <Wrench className="h-5 w-5 text-[#FF6B35]" />
                        </div>
                        <div>
                          <h3 className="text-sm font-bold text-white truncate max-w-[140px]">{service.serviceType}</h3>
                          <p className="text-xs text-gray-600 font-mono">{service.orderId}</p>
                        </div>
                      </div>
                      <span className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold border ${statusCfg.bg} ${statusCfg.text}`}>
                        <div className={`w-1.5 h-1.5 rounded-full ${statusCfg.dot} animate-pulse`} />
                        <span className="capitalize">{service.status}</span>
                      </span>
                    </div>

                    {/* Vehicle pill */}
                    <div className="bg-white/[0.04] border border-white/[0.06] rounded-xl p-3">
                      <p className="text-sm font-bold text-white">{service.vehicleNumber}</p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {service.vehicleBrand} {service.vehicleModel} • {service.fuelType}
                        {service.kilometer ? ` • ${service.kilometer} km` : ""}
                      </p>
                    </div>
                  </div>

                  {/* Progress bar */}
                  {stepIdx >= 0 && (
                    <div className="px-5 pb-4">
                      <div className="flex justify-between text-[10px] text-gray-600 mb-1.5">
                        <span>Booking Placed</span>
                        <span>Completed</span>
                      </div>
                      <div className="w-full bg-white/[0.06] rounded-full h-1">
                        <motion.div className="bg-[#FF6B35] h-1 rounded-full"
                          initial={{ width: 0 }} animate={{ width: `${progress}%` }}
                          transition={{ duration: 0.8, delay: i * 0.05 + 0.3 }} />
                      </div>
                    </div>
                  )}

                  {/* Details */}
                  <div className="px-5 pb-4 space-y-2.5 flex-1">
                    <div className="flex items-center gap-2 text-xs">
                      <Calendar className="h-3.5 w-3.5 text-[#FF6B35] flex-shrink-0" />
                      <span className="text-gray-400">{service.date}</span>
                      {service.time !== "N/A" && <span className="text-gray-600">• {service.time}</span>}
                    </div>
                    <div className="flex items-start gap-2 text-xs">
                      <MapPin className="h-3.5 w-3.5 text-gray-600 flex-shrink-0 mt-0.5" />
                      <span className={`font-semibold mr-1 ${isDoorstep ? "text-[#FF6B35]" : "text-gray-400"}`}>
                        {isDoorstep ? "Doorstep" : "Center"}:
                      </span>
                      <span className="text-gray-500 line-clamp-1">{service.location}</span>
                    </div>
                    {service.services.length > 0 && (
                      <div className="flex items-center gap-2 text-xs text-gray-600">
                        <List className="h-3.5 w-3.5 flex-shrink-0" />
                        <span>{service.services.length} service{service.services.length > 1 ? "s" : ""} booked</span>
                      </div>
                    )}

                    {/* Store/Status info */}
                    {service.status === "pending" ? (
                      <div className="flex items-center gap-2 p-2.5 bg-yellow-400/5 border border-yellow-400/15 rounded-xl text-xs">
                        <Clock className="h-3.5 w-3.5 text-yellow-400 flex-shrink-0" />
                        <span className="text-yellow-400 font-medium">Waiting for store acceptance</span>
                      </div>
                    ) : service.store ? (
                      <div className="flex items-center gap-2 p-2.5 bg-[#FF6B35]/5 border border-[#FF6B35]/15 rounded-xl text-xs">
                        <Truck className="h-3.5 w-3.5 text-[#FF6B35] flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="font-semibold text-white truncate">{service.store.name}</p>
                          {service.store.gst_number && <p className="text-[#FF6B35] font-mono text-[10px]">GST: {service.store.gst_number}</p>}
                        </div>
                      </div>
                    ) : service.centerLocation ? (
                      <div className="flex items-center gap-2 p-2.5 bg-green-400/5 border border-green-400/15 rounded-xl text-xs">
                        <MapPin className="h-3.5 w-3.5 text-green-400 flex-shrink-0" />
                        <span className="text-green-400 line-clamp-1">{service.centerLocation}</span>
                      </div>
                    ) : null}
                  </div>

                  {/* Amount + Footer */}
                  <div className="px-5 pb-5">
                    <div className="flex items-center justify-between py-3 border-t border-white/[0.06] mb-3">
                      <div>
                        <p className="text-[10px] text-gray-600 uppercase tracking-wide">Total Amount</p>
                        <p className="text-[10px] text-gray-600">{service.paymentMethod} • {service.paymentStatus}</p>
                      </div>
                      <div className="flex items-center text-xl font-black text-white">
                        <IndianRupee className="h-4 w-4 mr-0.5" />
                        <span>{service.amount.toLocaleString("en-IN")}</span>
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <button onClick={() => { setSelectedDetails(service); setShowDetailsModal(true); }}
                        className="flex-1 bg-[#FF6B35] text-white py-2.5 rounded-xl text-xs font-bold hover:bg-[#E85520] transition-colors shadow-lg shadow-[#FF6B35]/15">
                        View Details
                      </button>
                      {canViewInvoice(service) && (
                        <button onClick={() => navigate(`/invoice/${service.bookingId}`)}
                          className="flex-1 bg-white/[0.06] border border-white/10 text-white py-2.5 rounded-xl text-xs font-bold hover:bg-white/10 flex items-center justify-center gap-1.5 transition-colors">
                          <FileText className="h-3.5 w-3.5" /> Invoice
                        </button>
                      )}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Details Modal ────────────────────────────────────────────────────────── */}
      <AnimatePresence>
        {showDetailsModal && selectedDetails && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
            onClick={(e) => { if (e.target === e.currentTarget) setShowDetailsModal(false); }}>
            <motion.div initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }} transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
              className="bg-[#111111] border border-white/[0.08] rounded-2xl w-full max-w-2xl max-h-[92vh] flex flex-col shadow-2xl shadow-black/60">

              {/* Modal Header */}
              <div className="px-6 py-5 border-b border-white/[0.06] flex items-center justify-between flex-shrink-0">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#FF6B35]/10 rounded-xl flex items-center justify-center">
                    <Wrench className="h-5 w-5 text-[#FF6B35]" />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-white">{selectedDetails.serviceType}</h3>
                    <p className="text-xs text-gray-500 font-mono">{selectedDetails.orderId} • {selectedDetails.createdAt}</p>
                  </div>
                </div>
                <button onClick={() => setShowDetailsModal(false)}
                  className="w-8 h-8 bg-white/[0.06] hover:bg-white/10 rounded-xl flex items-center justify-center text-gray-400 hover:text-white transition-all">
                  <X className="h-4 w-4" />
                </button>
              </div>

              {/* Status + mini progress */}
              <div className="px-6 py-3 bg-white/[0.02] border-b border-white/[0.06] flex items-center justify-between flex-shrink-0">
                {(() => {
                  const cfg = getStatusConfig(selectedDetails.status);
                  return (
                    <span className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold border ${cfg.bg} ${cfg.text}`}>
                      <div className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
                      <span className="capitalize">{selectedDetails.bookingStatus}</span>
                    </span>
                  );
                })()}
                <div className="flex items-center gap-1">
                  {BOOKING_STEPS.map((step, idx) => {
                    const cur = getCurrentStep(selectedDetails.status);
                    return (
                      <div key={step.key} title={step.label}
                        className={`h-1.5 rounded-full transition-all ${idx <= cur ? "w-5 bg-[#FF6B35]" : "w-1.5 bg-white/10"}`} />
                    );
                  })}
                </div>
              </div>

              {/* Scrollable body */}
              <div className="px-6 py-5 overflow-y-auto flex-1 space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="bg-white/[0.04] border border-white/[0.06] rounded-xl p-4">
                    <p className="text-[10px] text-gray-600 uppercase tracking-wide mb-2">Vehicle</p>
                    <p className="font-bold text-white">{selectedDetails.vehicleNumber}</p>
                    <p className="text-sm text-gray-400">{selectedDetails.vehicleBrand} {selectedDetails.vehicleModel}</p>
                    <p className="text-xs text-gray-600">{selectedDetails.fuelType}{selectedDetails.kilometer ? ` • ${selectedDetails.kilometer} km` : ""}</p>
                  </div>
                  <div className="bg-white/[0.04] border border-white/[0.06] rounded-xl p-4">
                    <p className="text-[10px] text-gray-600 uppercase tracking-wide mb-2">Scheduled</p>
                    <p className="font-bold text-white">{selectedDetails.date}</p>
                    <p className="text-sm text-gray-400">{selectedDetails.time}</p>
                    <p className="text-xs text-gray-600">Booked: {selectedDetails.createdAt}</p>
                  </div>
                </div>

                <div className="bg-white/[0.04] border border-white/[0.06] rounded-xl p-4">
                  <p className="text-[10px] text-gray-600 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                    <MapPin className="h-3 w-3" /> Service Location
                  </p>
                  <p className="text-sm font-semibold text-white">{selectedDetails.location}</p>
                </div>

                <div className="bg-white/[0.04] border border-white/[0.06] rounded-xl p-4">
                  <p className="text-[10px] text-gray-600 uppercase tracking-wide mb-2">Issue / Comments</p>
                  <p className="text-sm text-gray-300">{selectedDetails.issueDescription}</p>
                </div>

                {/* Services list */}
                <div className="bg-white/[0.04] border border-white/[0.06] rounded-xl p-4">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-[10px] text-gray-600 uppercase tracking-wide flex items-center gap-1.5">
                      <List className="h-3 w-3 text-[#FF6B35]" /> Services ({selectedDetails.services?.length || 0})
                    </p>
                    {selectedDetails.services?.length > 3 && (
                      <button onClick={() => { setSelectedService(selectedDetails); setShowServicesModal(true); }}
                        className="text-xs text-[#FF6B35] font-semibold hover:underline">View All →</button>
                    )}
                  </div>
                  {selectedDetails.services?.length ? (
                    <div className="space-y-2">
                      {selectedDetails.services.slice(0, 3).map((svc, idx) => (
                        <div key={idx} className="flex items-center justify-between bg-white/[0.03] border border-white/[0.06] rounded-xl p-3">
                          <div>
                            <p className="text-sm font-semibold text-white">{svc.serviceName}</p>
                            <p className="text-xs text-gray-600">Qty: {svc.quantity}</p>
                          </div>
                          <p className="text-sm font-bold text-[#FF6B35] flex items-center">
                            <IndianRupee className="h-3.5 w-3.5" />{svc.servicePrice}
                          </p>
                        </div>
                      ))}
                      {selectedDetails.services.length > 3 && (
                        <p className="text-xs text-center text-gray-600 pt-1">+{selectedDetails.services.length - 3} more services</p>
                      )}
                    </div>
                  ) : <p className="text-sm text-gray-600 text-center py-3">No services listed</p>}
                </div>

                {/* Store/Center/Pending */}
                {selectedDetails.status === "pending" ? (
                  <div className="bg-yellow-400/5 border border-yellow-400/20 rounded-xl p-4">
                    <p className="text-xs text-yellow-400 font-bold mb-2 flex items-center gap-2"><Clock className="h-4 w-4" /> Status Update</p>
                    <p className="font-bold text-white mb-1">Waiting for Store</p>
                    <p className="text-sm text-gray-500">We'll notify you via SMS when a store accepts your booking</p>
                  </div>
                ) : selectedDetails.store ? (
                  <div className="bg-[#FF6B35]/5 border border-[#FF6B35]/20 rounded-xl p-4">
                    <p className="text-xs text-[#FF6B35] font-bold mb-3 flex items-center gap-2"><Truck className="h-4 w-4" /> Assigned Store</p>
                    <p className="text-base font-bold text-white mb-1">{selectedDetails.store.name}</p>
                    {selectedDetails.store.gst_number && (
                      <div className="inline-flex items-center gap-1.5 bg-white/5 px-3 py-1.5 rounded-lg border border-[#FF6B35]/20 mb-2">
                        <span className="text-xs font-bold text-[#FF6B35]">GST:</span>
                        <span className="text-sm font-mono text-white">{selectedDetails.store.gst_number}</span>
                      </div>
                    )}
                    <p className="text-sm text-gray-400 mb-2">{selectedDetails.store.address}</p>
                    <a href={`tel:${selectedDetails.store.contact}`}
                      className="inline-flex items-center gap-2 text-[#FF6B35] font-semibold text-sm hover:text-[#E85520]">
                      <Phone className="h-4 w-4" /> {selectedDetails.store.contact}
                    </a>
                  </div>
                ) : selectedDetails.centerLocation ? (
                  <div className="bg-green-400/5 border border-green-400/20 rounded-xl p-4">
                    <p className="text-xs text-green-400 font-bold mb-2 flex items-center gap-2"><MapPin className="h-4 w-4" /> Service Center</p>
                    <p className="text-sm font-semibold text-white">{selectedDetails.centerLocation}</p>
                  </div>
                ) : null}

                {/* Payment */}
                <div className="flex items-center justify-between bg-white/[0.04] border border-white/[0.06] rounded-xl p-4">
                  <div>
                    <p className="text-[10px] text-gray-600 uppercase tracking-wide">Payment</p>
                    <p className="text-sm font-semibold text-white">{selectedDetails.paymentMethod}</p>
                  </div>
                  <span className={`px-3 py-1 rounded-lg text-xs font-bold ${selectedDetails.paymentStatus === "Completed" ? "bg-green-400/10 text-green-400" : "bg-yellow-400/10 text-yellow-400"}`}>
                    {selectedDetails.paymentStatus}
                  </span>
                </div>

                {/* Total */}
                <div className="flex items-center justify-between p-4 bg-[#FF6B35] rounded-2xl shadow-lg shadow-[#FF6B35]/20">
                  <span className="font-bold text-lg text-white">Total Amount</span>
                  <span className="text-2xl font-black text-white flex items-center">
                    <IndianRupee className="h-5 w-5" />{selectedDetails.amount.toLocaleString("en-IN")}
                  </span>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="px-6 py-4 border-t border-white/[0.06] flex gap-3 flex-shrink-0">
                {canViewInvoice(selectedDetails) && (
                  <button onClick={() => navigate(`/invoice/${selectedDetails.bookingId}`)}
                    className="flex-1 bg-[#FF6B35] text-white py-3 rounded-xl font-semibold hover:bg-[#E85520] flex items-center justify-center gap-2 transition-colors">
                    <FileText className="h-4 w-4" /> View Invoice
                  </button>
                )}
                <button onClick={() => setShowDetailsModal(false)}
                  className="flex-1 bg-white/[0.06] border border-white/10 text-gray-300 py-3 rounded-xl font-semibold hover:bg-white/10 transition-colors">
                  Close
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── All Services Modal ───────────────────────────────────────────────────── */}
      <AnimatePresence>
        {showServicesModal && selectedService && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
            onClick={(e) => { if (e.target === e.currentTarget) setShowServicesModal(false); }}>
            <motion.div initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }} transition={{ duration: 0.25 }}
              className="bg-[#111111] border border-white/[0.08] rounded-2xl w-full max-w-lg max-h-[85vh] flex flex-col shadow-2xl shadow-black/60">
              <div className="px-6 py-5 border-b border-white/[0.06] flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 bg-[#FF6B35]/10 rounded-xl flex items-center justify-center">
                    <List className="h-4 w-4 text-[#FF6B35]" />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-white">All Services</h3>
                    <p className="text-xs text-gray-500 font-mono">{selectedService.orderId}</p>
                  </div>
                </div>
                <button onClick={() => setShowServicesModal(false)}
                  className="w-8 h-8 bg-white/[0.06] hover:bg-white/10 rounded-xl flex items-center justify-center text-gray-400 hover:text-white transition-all">
                  <X className="h-4 w-4" />
                </button>
              </div>

              <div className="px-6 py-4 overflow-y-auto flex-1 space-y-3">
                {selectedService.services?.map((svc, idx) => (
                  <motion.div key={idx} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: idx * 0.05 }}
                    className="flex items-start gap-3 p-4 bg-white/[0.04] border border-white/[0.06] rounded-xl">
                    <div className="w-8 h-8 bg-[#FF6B35] rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0 shadow-lg shadow-[#FF6B35]/20">
                      {idx + 1}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-white">{svc.serviceName}</p>
                      <span className="text-xs text-gray-600 bg-white/[0.04] px-2 py-0.5 rounded-full border border-white/[0.06] mt-1 inline-block">
                        Qty: {svc.quantity}
                      </span>
                      <p className="text-sm font-bold text-[#FF6B35] mt-2 flex items-center">
                        <IndianRupee className="h-3.5 w-3.5" />{svc.servicePrice}
                      </p>
                    </div>
                  </motion.div>
                ))}

                <div className="flex items-center justify-between p-4 bg-[#FF6B35] rounded-2xl mt-2 shadow-lg shadow-[#FF6B35]/20">
                  <span className="font-bold text-white">Total</span>
                  <span className="text-xl font-black text-white flex items-center">
                    <IndianRupee className="h-4 w-4" />{selectedService.amount.toLocaleString("en-IN")}
                  </span>
                </div>
              </div>

              <div className="px-6 py-4 border-t border-white/[0.06]">
                <button onClick={() => setShowServicesModal(false)}
                  className="w-full bg-white/[0.06] border border-white/10 text-gray-300 py-3 rounded-xl font-semibold hover:bg-white/10 transition-colors">
                  Close
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ServiceHistory;
